import React, { useEffect, useState } from "react"
import { FlatList, Image, ActivityIndicator } from "react-native"
import type { CompetitionProps, CompetitionRecordProps, CompetitionResultProps, CompetitionTypeProps, PublicPlayerProps } from "../../types"
import { CompetitionApi, CompetitionHelpers, CompetitionResultApi } from "../api"
import { Icons } from "../../Components"
import Colors from "../../constants/colors"
import { Button, Text, View } from "../../Components/Themed"
import { useColors } from "../../constants/useColors"
import Pagination from "../../Components/Pagination"

type CompetitionLeaderboardProps = {
    competition:CompetitionProps,
    competition_type:CompetitionTypeProps,
    hidden?:boolean,
    refresh_leaders:{
        refresh_key:string,
        competition_records:CompetitionRecordProps[],
        competition_results:CompetitionResultProps[]
    },
    disable_collapse?:boolean,
    viewer_id?:string,
    height: number,
    width:number,
    view_mode: 'short'|'full',
    onClose?:() => void,
    onSeeFull: () => void,
    onLeaderSelect:(player:PublicPlayerProps) => void
}
const sections = [ 'my_result','header', 'leaders' ]
const CompetitionLeaderboard = ({ hidden, refresh_leaders, disable_collapse, competition, competition_type, viewer_id, view_mode, onSeeFull, onClose, onLeaderSelect }:CompetitionLeaderboardProps) => {
    const C = useColors();
    const [ expanded, setExpanded ] = useState(true);
    const [ data, setData ] = useState<{
        loading:boolean,
        competition_results:CompetitionResultProps[],
        competition_records:CompetitionRecordProps[],
        players:PublicPlayerProps[],
        offset:number
    }>({
        loading:false,
        competition_results:[],
        competition_records:[],
        players:[],
        offset:0
    })

    const { loading, competition_results, competition_records, players, offset } = data
    let results = [ ...competition_results.filter(cr => cr.status != 'deleted') ];
    let records = [ ...competition_records.filter(cr => cr.status != 'deleted') ]
    
    if(view_mode == 'short'){ results = results.filter((r,i) => r ? i < 3 : i < 3) }

    let viewer = players.find(p => p.player_id == viewer_id);
    let viewer_result = results.find(r => r.player_id == viewer_id);
    let viewer_record = records.find(r => r.player_id == viewer_id);

    const cl = competition?.market_type == 'FOR_MONEY' ? '$' : 'E'

    useEffect(() => {
        if(!competition){ return }
        getLeaderboardFromServer(competition.competition_id, 0)
    },[competition?.competition_id]);

    useEffect(() => {
        if(refresh_leaders.competition_records.length == 0){ return }
        setData({
            ...data,
            competition_records: competition_records.filter(cr => !refresh_leaders.competition_records.find(newcr => newcr.competition_record_id == cr.competition_record_id)).concat(refresh_leaders.competition_records),
            competition_results: competition_results.filter(cr => !refresh_leaders.competition_results.find(newcr => newcr.competition_result_id == cr.competition_result_id)).concat(refresh_leaders.competition_results)
        })
    },[refresh_leaders?.refresh_key])

    const getLeaderboardFromServer = async(competition_id:string, offset:number) => {
        setData({ ...data, loading:true })
        const result_response = await CompetitionResultApi.getResultsByCompetition(competition_id, offset)
        let player_ids = result_response.competition_results.map(cr => cr.player_id)
        let players:PublicPlayerProps[] = []
        if(player_ids.length > 0){ players = await CompetitionApi.getPlayersByPlayerIds(player_ids) }
        setData({
            ...data,
            players,
            loading:false,
            competition_records: result_response.competition_records,
            competition_results: result_response.competition_results,
            offset,

        })
    }

    const renderResults = (data: {item:CompetitionResultProps, index:number}) => {
        let player:PublicPlayerProps | undefined = players.find(p => p.player_id == data.item.player_id)
        let record = competition_records.find(r => r.player_id == data.item.player_id)
        if(!player || !record){ return <></> }

        const is_pacer = competition.pacer_id == data.item.player_id ? true : false
        return (
            <Button 
                float
                onPress={() => onLeaderSelect(player)}
                style={{ flexDirection:'row', alignItems:'center', margin:3, borderRadius:22, padding:12, paddingLeft:15, paddingRight:15 }}>
                {is_pacer ?
                <View transparent nativeID="pacer">
                    <Icons.PacerCarIcon size={28} color={C.text.h1}/>
                </View>
                :
                <View transparent nativeID="place" style={{ padding:5 }}>
                    <Text theme="description">{CompetitionHelpers.formatPlace(data.item.place)}</Text>
                </View>
                }
                <View transparent nativeID="image" style={{ paddingLeft:10, paddingRight:10 }}>
                    <Image
                        source={{ uri: player.profile_pic }}
                        style={{ height:30, width:30, borderRadius:100 }}
                        resizeMode="cover"
                    />
                </View>
                <View transparent nativeID="details" style={{ flex:1 }}>
                    <Text size={12} theme="h2">{player.username}</Text>
                    {competition_type?.type == 'wager' ?
                    <Text style={{ marginTop:3 }} theme="description">{record.earnings.toFixed(2)} Earned ({record.remaining_stakes.toFixed(2)} at risk)</Text>
                    :
                    <Text style={{ marginTop:3 }} theme="description">{record.wins} - {record.losses} - {record.draws} ({record.remaining_picks} Remaining)</Text>
                    }
                </View>
                <View transparent nativeID="winnings">
                    <Text size={12} color={Colors.brand.slate} weight='regular'>{cl}{data.item.winnings.toFixed(2)}</Text>
                </View>
            </Button>
        )
    }

    const renderSections = (data: { item:string, index:number }) => {
        switch(data.item){
            case 'my_result':
                if(!viewer_record || !viewer_result || !viewer){ return <></> }
                return (
                    <View transparent style={{ padding:10, marginBottom:10, borderTopRightRadius:8, borderTopLeftRadius:8, borderBottomWidth:1, borderColor:C.borders.light }}>
                        <Text size={14} theme="h1">MY RESULTS</Text>
                        <Button 
                            float
                            style={{ flexDirection:'row', marginTop:10, marginBottom:10, alignItems:'center', borderRadius:22, padding:12, paddingLeft:15, paddingRight:15 }}>
                            <View transparent nativeID="place" style={{ padding:5 }}>
                                <Text size={12} theme='h2'>{CompetitionHelpers.formatPlace(viewer_result.place)}</Text>
                            </View>
                            <View transparent nativeID="image" style={{ paddingLeft:10, paddingRight:10 }}>
                                <Image
                                    source={{ uri: viewer.profile_pic }}
                                    style={{ height:30, width:30, borderRadius:100 }}
                                    resizeMode="cover"
                                />
                            </View>
                            <View transparent nativeID="details" style={{ flex:1 }}>
                                <Text size={12} theme="h2">{viewer.username}</Text>
                                {competition_type?.type == 'wager' ?
                                    <Text style={{ marginTop:3 }} theme='description'>{viewer_record.earnings.toFixed(2)} Earned ({viewer_record.remaining_stakes.toFixed(2)} at risk)</Text>
                                    :
                                    <Text style={{ marginTop:3 }} theme='description'>{viewer_record.wins} - {viewer_record.losses} - {viewer_record.draws} ({viewer_record.remaining_picks} Remaining)</Text>
                                }
                            </View>
                            <View nativeID="winnings">
                                <Text size={12} theme="light" weight='bold'>{cl}{viewer_result.winnings.toFixed(2)}</Text>
                            </View>
                        </Button>
                    </View>
                )
            case 'leaders':
                return (
                     <FlatList
                        data={results}
                        renderItem={renderResults}
                        keyExtractor={(item, index) => item.competition_result_id ? item.competition_result_id.toString() : index.toString()}
                    />
                )
            default: return <></>
        }
    }

    if(hidden){ return <></> }

    if(view_mode == 'short'){
        return (
            <View nativeID="leaderboard" style={{ flex:1 }}>
             <Button 
                disabled={disable_collapse}
                style={{ flexDirection:'row', alignItems:'center', borderBottomWidth:expanded?1:0, borderColor:C.borders.light }}
                transparent
                onPress={() => setExpanded(!expanded)}>
                    <Icons.SettingsIcon size={18} color={C.text.h1} />
                    <View transparent style={{ flex:1, marginLeft:10 }}>
                        <Text theme="h1">COMPETITION LEADERBOARD</Text>
                        <Text style={{ marginTop: 4 }} theme="description">Leaderboard showing current standings</Text>
                    </View>
                    {!disable_collapse ?
                    <Icons.ChevronIcon direction={expanded?'up':'down'} color={C.text.h1} size={8} />
                    :<></>}
                </Button>
                {expanded ?
                <View transparent type='body'>
                    {viewer_record && viewer_result && viewer ?
                    <View transparent style={{ padding:10, marginBottom:10, borderTopRightRadius:8, borderTopLeftRadius:8, borderBottomWidth:1, borderColor:C.borders.light }}>
                        <Text size={14} theme="h1">MY RESULTS</Text>
                        <Button 
                            float
                            style={{ flexDirection:'row', marginTop:10, marginBottom:10, alignItems:'center', borderRadius:22, padding:12, paddingLeft:15, paddingRight:15 }}>
                            <View transparent nativeID="place" style={{ padding:5 }}>
                                <Text size={12} theme='h2'>{CompetitionHelpers.formatPlace(viewer_result.place)}</Text>
                            </View>
                            <View transparent nativeID="image" style={{ paddingLeft:10, paddingRight:10 }}>
                                <Image
                                    source={{ uri: viewer.profile_pic }}
                                    style={{ height:30, width:30, borderRadius:100 }}
                                    resizeMode="cover"
                                />
                            </View>
                            <View transparent nativeID="details" style={{ flex:1 }}>
                                <Text size={12} theme="h2">{viewer.username}</Text>
                                {competition_type?.type == 'wager' ?
                                    <Text style={{ marginTop:3 }} theme='description'>{viewer_record.earnings.toFixed(2)} Earned ({viewer_record.remaining_stakes.toFixed(2)} at risk)</Text>
                                    :
                                    <Text style={{ marginTop:3 }} theme='description'>{viewer_record.wins} - {viewer_record.losses} - {viewer_record.draws} ({viewer_record.remaining_picks} Remaining)</Text>
                                }
                            </View>
                            <View nativeID="winnings">
                                <Text size={12} theme="light" weight='bold'>{cl}{viewer_result.winnings.toFixed(2)}</Text>
                            </View>
                        </Button>
                    </View>
                :<></>}
            </View>
            :<></>}
             <View transparent style={{ padding:10, flex:1 }}>
                {loading ?
                <ActivityIndicator style={{ alignSelf:'center', padding:10 }} size='large' color={C.text.h1} /> 
                :<></>}
                <FlatList
                    data={results}
                    renderItem={renderResults}
                    keyExtractor={(item, index) => item.competition_result_id ? item.competition_result_id.toString() : index.toString()}
                />
                
            </View>

            <Button
                style={{ backgroundColor:undefined }}
                title='View Full Leaderboard'
                type='text'
                onPress={() => onSeeFull()}
            />
            </View>
        )
    }

    return (
        <View float style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key={'full_leaders_list'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
            </View>
            <View type='footer' style={{ padding:10, borderBottomRightRadius:22, borderBottomLeftRadius:22 }}>
                <Pagination
                    offset={offset}
                    onNext={() => getLeaderboardFromServer(competition.competition_id, offset+1)}
                    onPrevious={() => getLeaderboardFromServer(competition.competition_id, offset-1)}

                />
                <Button
                    title='Close'
                    style={{ padding:15 }}
                    type='close'
                    onPress={() => onClose ? onClose() : console.log('')}
                />
            </View>
        </View>
    )
}

export default CompetitionLeaderboard